import {
  OperationVariables,
  PreloadQueryOptions,
  QueryReference,
  TypedDocumentNode,
  UseReadQueryResult,
  useReadQuery,
} from '@apollo/client';
import { captureMessage } from '@sentry/react';
import { ComponentType } from 'react';
import { Params, useLoaderData } from 'react-router-dom';

import { preloadQuery } from 'contexts/graphql/Provider';

import shouldRevalidateFn from './shouldRevalidateFn';

type GetOptions<
  PageOrLayoutParams extends Params,
  TVariables extends OperationVariables,
> =
  Values<TVariables> extends never
    ? [void | ((args: PageOrLayoutParams) => PreloadQueryOptions<never>)]
    : [(args: PageOrLayoutParams) => PreloadQueryOptions<TVariables>];
type DataFunctionValue = Response | NonNullable<unknown> | null;

type LoaderFunction<PageOrLayoutParams extends Params> = (args: {
  params: PageOrLayoutParams;
}) => Promise<DataFunctionValue> | DataFunctionValue;

export const next_withRouteQuery = <
  PageOrLayoutParams extends Params,
  P,
  TData,
  TVariables extends OperationVariables,
>(
  Component: ComponentType<P & { queryResult: UseReadQueryResult<TData> }>,
  query: TypedDocumentNode<TData, TVariables>,
  ...[getOptions]: GetOptions<PageOrLayoutParams, NoInfer<TVariables>>
): ComponentType<P> & {
  loader: LoaderFunction<PageOrLayoutParams>;
} => {
  const loader = async ({ params }: { params: PageOrLayoutParams }) => {
    if (!preloadQuery.current) {
      captureMessage('Apollo preloadQuery is not initialized');
      return null;
    }
    const preloadedQueryRef = preloadQuery.current(
      query,
      // this apollo version types badly PreloadQueryFunction so an {} object is needed
      // even when the query asks for no variables
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      getOptions?.(params) || {}
    );

    return preloadedQueryRef;
  };

  const PreloadedComponent: ComponentType<
    P & { preloadedQueryRef: QueryReference<TData, TVariables> }
  > = ({ preloadedQueryRef, ...otherProps }) => {
    const queryResult = useReadQuery<TData>(preloadedQueryRef);
    return <Component {...(otherProps as P)} queryResult={queryResult} />;
  };

  const ComponentWithRouteQuery = (props: P) => {
    const preloadedQueryRef = useLoaderData() as QueryReference<
      TData,
      TVariables
    > | null;

    if (!preloadedQueryRef) {
      return null;
    }
    return (
      <PreloadedComponent {...props} preloadedQueryRef={preloadedQueryRef} />
    );
  };

  return Object.assign(ComponentWithRouteQuery, {
    loader,
    shouldRevalidate: getOptions
      ? shouldRevalidateFn(params => getOptions(params).variables!)
      : undefined,
  });
};
